import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Canvas, CanvasShareKind } from '@avslack/shared';
import {
  createCanvasApi,
  deleteCanvasApi,
  listCanvases,
  listCanvasSharesApi,
  reorderCanvasesApi,
  shareCanvasApi,
  unshareCanvasApi,
  updateCanvasApi,
} from '../lib/api';

/**
 * A csatorna canvasainak ADATLAPJAI.
 *
 * A tartalom nem itt van - azt a Yjs-kapcsolat hozza (`lib/canvas-sync.ts`).
 * Ez a lista csak cimet, kiemelest es elonezetet tart, ezert olcso.
 */
export const canvasesKey = (channelId: string) => ['canvases', channelId] as const;

export function useCanvases(channelId: string, enabled = true) {
  return useQuery({
    queryKey: canvasesKey(channelId),
    queryFn: () => listCanvases(channelId),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateCanvas(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; isPrimary?: boolean }) =>
      createCanvasApi(channelId, input),
    onSuccess: (canvas) => upsertCanvas(queryClient, canvas),
  });
}

export function useUpdateCanvas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      canvasId,
      patch,
    }: {
      canvasId: string;
      patch: { title?: string; isPrimary?: boolean };
    }) => updateCanvasApi(canvasId, patch),
    onSuccess: (canvas) => {
      upsertCanvas(queryClient, canvas);
      // Megosztott canvas: mas listakban (csatorna, "Velem megosztva") is szerepel.
      refreshSharedCanvases(queryClient);
    },
  });
}

// ─── Megosztas (szemelyes canvas) ─────────────────────────────────────────────

export const canvasSharesKey = (canvasId: string) => ['canvas-shares', canvasId] as const;

export function useCanvasShares(canvasId: string, enabled = true) {
  return useQuery({
    queryKey: canvasSharesKey(canvasId),
    queryFn: () => listCanvasSharesApi(canvasId),
    enabled,
  });
}

export function useShareCanvas(canvasId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: CanvasShareKind; targetId: string }) =>
      shareCanvasApi(canvasId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: canvasSharesKey(canvasId) }),
  });
}

export function useUnshareCanvas(canvasId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => unshareCanvasApi(canvasId, shareId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: canvasSharesKey(canvasId) }),
  });
}

/**
 * Egy megosztott canvas valtozott (`canvas.shared`): hogy melyik listaban
 * szerepel, azt a kliens nem tudja - ezert minden canvas-listat ujratolt.
 * Ritka esemeny, es csak a betoltott listak kernek ujra.
 */
export function refreshSharedCanvases(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['canvases'] });
  void queryClient.invalidateQueries({ queryKey: ['canvas-shares'] });
}

/**
 * SORREND huzas utan - azonnal atrendezzuk a listat (optimista), a szerver
 * valasza es a tobbi tagnak kimeno `canvas.reordered` ugyanazt allitja be.
 * Hiba eseten a szerver szerinti lista jon vissza.
 */
export function useReorderCanvases(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasIds: string[]) => reorderCanvasesApi(channelId, canvasIds),
    onMutate: (canvasIds) => applyCanvasOrder(queryClient, channelId, canvasIds),
    onSuccess: (canvasIds) => applyCanvasOrder(queryClient, channelId, canvasIds),
    onError: () => void queryClient.invalidateQueries({ queryKey: canvasesKey(channelId) }),
  });
}

/** A kapott sorrend beirasa (sajat huzas vagy egy kollegae a `canvas.reordered` kereten). */
export function applyCanvasOrder(
  queryClient: QueryClient,
  channelId: string,
  canvasIds: string[],
): void {
  const rank = new Map(canvasIds.map((id, index) => [id, index + 1]));
  queryClient.setQueryData<Canvas[]>(canvasesKey(channelId), (current) =>
    current
      ? sortCanvases(
          current.map((item) => ({
            ...item,
            sortOrder: rank.get(item.id) ?? canvasIds.length + item.sortOrder,
          })),
        )
      : current,
  );
}

export function useDeleteCanvas(channelId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) => deleteCanvasApi(canvasId),
    onSuccess: (_result, canvasId) => removeCanvas(queryClient, channelId, canvasId),
  });
}

/**
 * Egy canvas beirasa a listaba.
 *
 * A rendezes ITT is a szerver szabalyat koveti (kiemeltek elol, azon belul a
 * csatorna kozos sorrendje) - kulonben egy frissen letrehozott canvas mashova
 * ugrana, mint ahova a kovetkezo ujratoltesnel kerul.
 */
export function upsertCanvas(queryClient: QueryClient, canvas: Canvas): void {
  queryClient.setQueryData<Canvas[]>(canvasesKey(canvas.channelId), (current) => {
    /*
      A megosztott canvas CIMZETTJE (pl. atnevezes utan) a tulajdonos listajat
      nem latja - abbol ne keletkezzen nala egy egyelemu, hamis lista.
    */
    if (!current && canvas.shareCount > 0) return current;
    const rest = (current ?? []).filter((item) => item.id !== canvas.id);
    return sortCanvases([...rest, canvas]);
  });
}

export function removeCanvas(queryClient: QueryClient, channelId: string, canvasId: string): void {
  queryClient.setQueryData<Canvas[]>(canvasesKey(channelId), (current) =>
    (current ?? []).filter((item) => item.id !== canvasId),
  );
}

export function sortCanvases(list: Canvas[]): Canvas[] {
  return [...list].sort((a, b) => {
    if (a.viaShare !== b.viaShare) return a.viaShare ? 1 : -1;
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

/** A csatorna kiemelt canvasai, a kozos sorrendben - az elso kerul a fejlec buborekjaba. */
export function primaryCanvases(list: Canvas[] | undefined): Canvas[] {
  return list?.filter((item) => item.isPrimary && !item.viaShare) ?? [];
}
